import { Row, Select, Separator, H3 } from '@fs/zion-ui'
import React, { useContext } from 'react'
import { useTranslation } from 'react-i18next'
import useTempleDirectory from '../../../../hooks/useTempleDirectory'
import { getBooleanDisplayValue, getLastPathElement } from '../../../../util/StringUtil'
import { PermissionsContext } from '../../../app/PermissionsContext'
import DmcDataView from '../../../common/DmcDataView'
import { DmcDatePicker } from '../../../common/DmcDatePicker/DmcDatePicker'
import DmcTemplePicker from '../../../common/DmcTemplePicker'
import Editable from '../../../common/Editable'
import CtrFlag from '../../CtrFlag'
import CreateTreePersonButton from '../createTreePerson/CreateTreePersonButton'
import CtrSummaryEditButton from '../edit/CtrSummaryEditButton'

export default function CtrSummaryDetails({
  ogTemplePlatform,
  editableTemplePlatform,
  setEditableTemplePlatform,
  ogCtrExtendedDetail,
  editableCtrExtendedDetail,
  setEditableCtrExtendedDetail,
  isEditing,
  setEditing,
  verifiedPersons,
  setVerifiedPersons,
  verifiedMembers,
  setVerifiedMembers,
}) {
  const { t } = useTranslation()
  const { isAuthorized, isRole } = useContext(PermissionsContext)
  const ctrContainsLivingPerson = editableTemplePlatform.persons.some((person) => {
    if (person.id.includes('qr.')) {
      return false
    }
    return person.living || person.living === undefined || person.living === null
  })

  if (!editableTemplePlatform.ctrs[0].id) {
    return null
  }
  return (
    <>
      <Row>
        <H3 size="H5" growX>
          {t('ctr.summary.details', 'CTR Details')}
        </H3>
        {!ctrContainsLivingPerson && isRole(['Data Admin 5', 'Data Admin 6']) && (
          <CreateTreePersonButton templePlatform={ogTemplePlatform} />
        )}
        {isAuthorized('tss_ModifyCtrPermission') && (
          <CtrSummaryEditButton
            ogTemplePlatform={ogTemplePlatform}
            editableTemplePlatform={editableTemplePlatform}
            setEditableTemplePlatform={setEditableTemplePlatform}
            ogCtrExtendedDetail={ogCtrExtendedDetail}
            editableCtrExtendedDetail={editableCtrExtendedDetail}
            setEditableCtrExtendedDetail={setEditableCtrExtendedDetail}
            isEditing={isEditing}
            setEditing={setEditing}
            verifiedPersons={verifiedPersons}
            setVerifiedPersons={setVerifiedPersons}
            verifiedMembers={verifiedMembers}
            setVerifiedMembers={setVerifiedMembers}
          />
        )}
      </Row>
      <Separator size="xs" />
      <Details
        editableTemplePlatform={editableTemplePlatform}
        setEditableTemplePlatform={setEditableTemplePlatform}
        editableCtrExtendedDetail={editableCtrExtendedDetail}
        setEditableCtrExtendedDetail={setEditableCtrExtendedDetail}
        isEditing={isEditing}
      />
    </>
  )
}

function Details({
  editableTemplePlatform,
  setEditableTemplePlatform,
  editableCtrExtendedDetail,
  setEditableCtrExtendedDetail,
  isEditing,
}) {
  const { t } = useTranslation()
  const { temples } = useTempleDirectory()
  const ctr = editableTemplePlatform.ctrs[0]
  const ordinanceType = ctr?.ordinanceType ? editableTemplePlatform.ctrs[0]?.ordinanceType : ''
  const templeCode = ctr?.templeRef?.templeCode || ''
  const temple = templeCode && temples?.[templeCode]
  const performedDate = ctr?.performedDate
  const performedDateItem = {
    key: 'performedDate',
    formal: performedDate?.formal ? performedDate?.formal : '',
    original: performedDate?.original ? performedDate?.original : '',
    primaryText: performedDate?.original ? performedDate?.original : '',
  }
  const status = editableTemplePlatform.ctrs[0]?.ordinanceStatus ? editableTemplePlatform.ctrs[0]?.ordinanceStatus : ''
  const originalDate = editableCtrExtendedDetail.originalDate ? editableCtrExtendedDetail.originalDate : ''
  const hidden = editableCtrExtendedDetail.hidden
  const createdDate = ctr.createDate?.original ? ctr.createDate?.original : ''
  const officialType = editableCtrExtendedDetail.officialType ? editableCtrExtendedDetail.officialType : ''
  const updatedDate = ctr.updateDate?.original ? ctr.updateDate?.original : ''
  const recordedDate = editableCtrExtendedDetail.recordedTimestamp
    ? editableCtrExtendedDetail.recordedTimestamp?.original
    : ''

  const ctrData = [
    {
      label: <b>{t('type', 'Type')}</b>,
      detail: (
        <Editable
          text={t(`ctr.ordinanceType.${getLastPathElement(ordinanceType)}`, getLastPathElement(ordinanceType))}
          isEdit={isEditing}
        >
          <div style={{ display: 'inline-block' }}>
            <Select
              id="ctrType"
              value={ordinanceType}
              onChange={(event) => {
                const newTemplePlatform = {
                  ...editableTemplePlatform,
                }
                newTemplePlatform.ctrs[0].ordinanceType = event.target.value
                setEditableTemplePlatform(newTemplePlatform)
              }}
              options={[
                {
                  value: 'http://api.familysearch.org/temple/BAPTISM_LDS',
                  label: t('ctr.ordinanceType.BAPTISM_LDS', 'Baptism'),
                },
                {
                  value: 'http://api.familysearch.org/temple/LDS_LICENSE_MARRIAGE',
                  label: t('ctr.ordinanceType.LDS_LICENSE_MARRIAGE', 'LDS License Marriage'),
                },
                {
                  value: 'http://api.familysearch.org/temple/CONFIRMATION_LDS',
                  label: t('ctr.ordinanceType.CONFIRMATION_LDS', 'LDS Confirmation'),
                },
                {
                  value: 'http://api.familysearch.org/temple/INITIATORY',
                  label: t('ctr.ordinanceType.INITIATORY', 'Initiatory1'),
                },
                {
                  value: 'http://api.familysearch.org/temple/ENDOWMENT',
                  label: t('ctr.ordinanceType.ENDOWMENT', 'Endowment'),
                },
                {
                  value: 'http://api.familysearch.org/temple/SEALING_TO_PARENTS',
                  label: t('ctr.ordinanceType.SEALING_TO_PARENTS', 'Sealing to Parents'),
                },
                {
                  value: 'http://api.familysearch.org/temple/SEALING_TO_SPOUSE',
                  label: t('ctr.ordinanceType.SEALING_TO_SPOUSE', 'Sealing to Spouse'),
                },
                {
                  value: 'http://api.familysearch.org/temple/TIME_ONLY_MARRIAGE',
                  label: t('ctr.ordinanceType.TIME_ONLY_MARRIAGE', 'Time-only Marriage'),
                },
              ]}
            />
          </div>
        </Editable>
      ),
      key: 'type',
    },
    {
      label: <b>{t('temple.label', 'Temple')}</b>,
      detail: (
        <Editable text={temple} isEdit={isEditing} type={temple ? 'Temple' : ''}>
          <DmcTemplePicker
            label={null}
            onSuggestionSelected={(selection) => {
              const newTemplePlatform = {
                ...editableTemplePlatform,
              }
              if (!selection) {
                delete newTemplePlatform.ctrs[0].templeRef
              } else {
                newTemplePlatform.ctrs[0].templeRef = {
                  ...newTemplePlatform.ctrs[0].templeRef,
                  templeCode: selection.templeCode,
                }
              }
              setEditableTemplePlatform(newTemplePlatform)
            }}
            onClear={() => {
              const newTemplePlatform = {
                ...editableTemplePlatform,
              }
              delete newTemplePlatform.ctrs[0].templeRef
              setEditableTemplePlatform(newTemplePlatform)
            }}
            templeCode={templeCode}
          />
        </Editable>
      ),
      key: 'temple',
    },
    {
      label: isEditing ? null : <b>{t('ctr.performedDate', 'Performed Date')}</b>,
      key: 'performedDate',
      detail: (
        <Editable text={performedDateItem.original} isEdit={isEditing}>
          <DmcDatePicker
            idType="performed"
            label={t('ctr.performedDate', 'Performed Date')}
            selectedItem={performedDateItem}
            setSelectedItem={(e) => {
              if (e) {
                const newTemplePlatform = {
                  ...editableTemplePlatform,
                }
                if (e.formal === '' && e.original === '' && e.primaryText === '') {
                  delete newTemplePlatform.ctrs[0].performedDate
                } else {
                  newTemplePlatform.ctrs[0].performedDate = {
                    formal: e.formal,
                    original: e.original,
                  }
                }
                setEditableTemplePlatform(newTemplePlatform)
              }
            }}
            dayMonthYearDateFormat="yyyy-MM-DD"
          />
        </Editable>
      ),
    },
    {
      label: <b>{t('status.label', 'Status')}</b>,
      key: 'status',
      detail: (
        <Editable
          text={t(`ctr.ordinanceStatus.${getLastPathElement(status)}`, getLastPathElement(status))}
          isEdit={isEditing}
        >
          <div style={{ display: 'inline-block' }}>
            <Select
              id="ctrStatus"
              value={status}
              onChange={(event) => {
                const newTemplePlatform = {
                  ...editableTemplePlatform,
                }
                newTemplePlatform.ctrs[0].ordinanceStatus = event.target.value
                setEditableTemplePlatform(newTemplePlatform)
              }}
              options={[
                {
                  label: t('ctr.ordinanceStatus.INVALID', 'Invalid'),
                  value: 'http://api.familysearch.org/temple/INVALID',
                },
                {
                  label: t('ctr.ordinanceStatus.CANCELLED', 'Cancelled'),
                  value: 'http://api.familysearch.org/temple/CANCELLED',
                },
                {
                  label: t('ctr.ordinanceStatus.COMPLETED', 'Completed'),
                  value: 'http://api.familysearch.org/temple/COMPLETED',
                },
              ]}
            />
          </div>
        </Editable>
      ),
    },
    {
      label: null,
      detail: null,
      key: 'spacer1',
    },
    {
      label: <b>{t('ctr.originalDate', 'Original Date')}</b>,
      detail: originalDate,
      key: 'originalDate',
    },
    {
      label: <b>{t('hidden', 'Hidden')}</b>,
      key: 'hidden',
      detail: (
        <Editable text={getBooleanDisplayValue(hidden)} isEdit={isEditing}>
          <CtrFlag
            value={hidden}
            onChange={(value) => {
              setEditableCtrExtendedDetail({
                ...editableCtrExtendedDetail,
                hidden: value === 'Yes',
              })
            }}
            name="ctr-hidden"
          />
        </Editable>
      ),
    },
    {
      label: null,
      detail: null,
      key: 'spacer2',
    },
    {
      label: <b>{t('createdDate', 'Created Date')}</b>,
      detail: createdDate,
      key: 'createdDate',
    },
    {
      label: <b>{t('ctr.officialType', 'Official Type')}</b>,
      key: 'officialType',
      detail: (
        <Editable
          text={
            officialType?.length > 0
              ? t(`ctr.officialType.${getLastPathElement(officialType)}`, getLastPathElement(officialType))
              : getLastPathElement(officialType)
          }
          isEdit={isEditing}
        >
          <div style={{ display: 'inline-block' }}>
            <Select
              id="officialType"
              value={officialType}
              onChange={(event) => {
                setEditableCtrExtendedDetail({
                  ...editableCtrExtendedDetail,
                  officialType: event.target.value,
                })
              }}
              options={[
                {
                  value: 'http://api.familysearch.org/temple/OFFICIAL_PRIMARY',
                  label: t('ctr.officialType.OFFICIAL_PRIMARY', 'Official Primary'),
                },
                {
                  value: 'http://api.familysearch.org/temple/OFFICIAL_SECONDARY',
                  label: t('ctr.officialType.OFFICIAL_SECONDARY', 'Official Secondary'),
                },
                {
                  value: 'http://api.familysearch.org/temple/OFFICIAL_UNDETERMINED',
                  label: t('ctr.officialType.OFFICIAL_UNDETERMINED', 'Official Undetermined'),
                },
                {
                  value: 'http://api.familysearch.org/temple/OFFICIAL_RESTORATION_OF_BLESSINGS',
                  label: t('ctr.officialType.OFFICIAL_RESTORATION_OF_BLESSINGS', 'Official Restoration Of Blessings'),
                },
              ]}
            />
          </div>
        </Editable>
      ),
    },
    {
      label: null,
      detail: null,
      key: 'spacer3',
    },
    {
      label: <b>{t('updated', 'Updated')}</b>,
      key: 'updatedDate',
      detail: updatedDate,
    },
    {
      label: null,
      detail: null,
      key: 'spacer4',
    },
    {
      label: null,
      detail: null,
      key: 'spacer5',
    },
    {
      label: <b>{t('ctr.recorded', 'Recorded')}</b>,
      detail: recordedDate,
      key: 'recordedDate',
    },
  ]

  return <DmcDataView id="CTR Details" data={ctrData} />
}
